import { Badge, Button, Icon, Table, Text, Tooltip } from '@ui/themed';
import { t } from 'i18next';
import { type PropsWithChildren, useCallback, useMemo } from 'react';
import { LimitChildren, type LimitChildrenProps } from '../../../components/limit-children';
import type { BadgeProps } from '../../../components/ui/badge/types';
import { useEnv } from '../../../context/env-context';
import { useHandlers } from '../../../context/page-context';
import { PRICE_LIST_SOURCES } from '../../../features/price-lists';
import {
  type ColumnId,
  CUSTOMER_GROUP_BASE_SLUG,
  resolveColumnHeadingKey,
  TABLE_COLUMNS,
} from '../constants';
import { useCustomerGroupTag } from '../hooks/use-customer-group-tag';
import type { CustomerGroup } from '../types/customer-group-response';

const createLimitChildrenProps = (isInherited?: boolean): LimitChildrenProps => ({
  limit: isInherited ? undefined : 2,
  overflowElement: (remaining, hiddenChildren) => (
    <Tooltip
      key="extra"
      content={<div className="flex flex-col items-center gap-2">{hiddenChildren}</div>}
    >
      <Badge interactive variant="default">{`+ ${remaining}`}</Badge>
    </Tooltip>
  ),
});

export function ResultsElement({
  data,
  isInherited,
  noLimit,
}: {
  data: Array<string> | undefined;
  isInherited?: boolean;
  noLimit?: boolean;
}) {
  if (!data?.length) {
    return <Badge variant="default">{t('labels:none')}</Badge>;
  }
  return (
    // If inheritance occurs, or noLimit is set, do not limit the number of displayed items
    <LimitChildren {...createLimitChildrenProps(isInherited || noLimit)}>
      {data.map((method) => (
        <Badge key={method} variant="default">
          {method}
        </Badge>
      ))}
    </LimitChildren>
  );
}

function MethodTooltipWrapper({
  children,
  parentName,
}: PropsWithChildren<{
  parentName?: string;
}>) {
  if (!children) {
    return null;
  }

  // Only show tooltip if there is a parent group and it's not the base group
  if (!parentName) {
    return children;
  }

  return (
    <Tooltip
      content={
        <div className="mb-1 flex flex-start flex-col gap-2">
          <span className="text-spark-text-secondary">
            {t(
              'features:customer_group.components.customer_groups_table.descriptions.tooltips.inherit',
              {
                parent_name: parentName,
              },
            )}
          </span>
          {children}
        </div>
      }
    >
      <Badge interactive variant="white">
        Inheriting
      </Badge>
    </Tooltip>
  );
}

type PriceListType = 'manual' | 'automatic' | 'integration' | 'platform' | 'more' | 'none';

type PriceListDisplay = {
  name: string;
  type: PriceListType;
};

const BADGE_CONFIG: Record<PriceListType, BadgeProps> = {
  more: { variant: 'default' },
  none: { variant: 'default' },
  automatic: { variant: 'purple', icon: 'refresh' },
  manual: { variant: 'blue', icon: 'upload' },
  integration: { variant: 'orange', icon: 'menu' },
  platform: {},
} as const;

export function PriceListBadges({ group }: { group: CustomerGroup }) {
  const getType = useCallback(
    (name: string): PriceListType => {
      const list = group?.priceLists?.find((list) => list.name === name);
      if (!list) {
        return 'none';
      }

      if (name === '+ ') {
        return 'more';
      }

      if (
        list.source === PRICE_LIST_SOURCES.INTEGRATION ||
        list.source === PRICE_LIST_SOURCES.PLATFORM
      ) {
        return list.source;
      }

      if (list?.rules.length > 0) {
        return 'automatic';
      }

      return 'manual';
    },
    [group?.priceLists],
  );

  const isInherited = group.inheritList?.includes('priceListSelection');

  const priceLists = useMemo<PriceListDisplay[]>(() => {
    const priceListNames = group?.priceLists?.map((list) => list.name);
    if (!priceListNames) {
      return [];
    }

    return priceListNames.map((name) => ({
      name: name,
      type: getType(name),
    }));
  }, [group?.priceLists, getType]);

  if (!group?.priceLists || group.priceLists.length === 0) {
    return (
      <MethodTooltipWrapper parentName={isInherited ? group.parent_name : undefined}>
        <Badge variant="default">{t('labels:none')}</Badge>
      </MethodTooltipWrapper>
    );
  }

  return (
    <MethodTooltipWrapper parentName={isInherited ? group.parent_name : undefined}>
      <Badge variant="default">{group?.priceLists[0]?.currency_code ?? t('labels:none')}</Badge>
      <LimitChildren {...createLimitChildrenProps(isInherited)}>
        {priceLists.map(({ name, type }) => (
          <Badge key={name} truncate {...BADGE_CONFIG[type]}>
            {name}
          </Badge>
        ))}
      </LimitChildren>
    </MethodTooltipWrapper>
  );
}

function OrderLimitsBadges({ group }: { group: CustomerGroup }) {
  const inheritTotals = group?.inheritList?.includes('orderTotalsValidation');
  const inheritQuantity = group?.inheritList?.includes('orderItemQuantityValidation');

  if (inheritTotals && inheritQuantity) {
    return (
      <MethodTooltipWrapper parentName={group.parent_name}>
        <ResultsElement
          isInherited
          data={(group?.orderQuantityLimits ?? []).concat(group?.orderTotalLimits ?? [])}
        />
      </MethodTooltipWrapper>
    );
  }

  if (inheritTotals && group.orderQuantityLimits) {
    return (
      <div className="flex gap-3">
        {group.orderQuantityLimits && (
          <MethodTooltipWrapper>
            <ResultsElement data={group.orderQuantityLimits} />
          </MethodTooltipWrapper>
        )}
        {group.orderTotalLimits && (
          <MethodTooltipWrapper parentName={group.parent_name}>
            <ResultsElement isInherited data={group.orderTotalLimits} />
          </MethodTooltipWrapper>
        )}
      </div>
    );
  }

  if (inheritQuantity && group.orderTotalLimits) {
    return (
      <div className="flex gap-3">
        {group.orderQuantityLimits && (
          <MethodTooltipWrapper parentName={group.parent_name}>
            <ResultsElement isInherited data={group.orderQuantityLimits} />
          </MethodTooltipWrapper>
        )}
        {group.orderTotalLimits && (
          <MethodTooltipWrapper>
            <ResultsElement data={group.orderTotalLimits} />
          </MethodTooltipWrapper>
        )}
      </div>
    );
  }

  return (
    <MethodTooltipWrapper>
      <ResultsElement
        data={(group?.orderQuantityLimits ?? []).concat(group?.orderTotalLimits ?? [])}
      />
    </MethodTooltipWrapper>
  );
}

function CustomerGroupsTableRow({
  group,
  index,
  visibleColumns,
}: {
  group: CustomerGroup;
  index: number;
  visibleColumns: Set<ColumnId>;
}) {
  const { navigationHandler } = useHandlers();
  const { tags } = useCustomerGroupTag(group.slug);
  const show = (id: ColumnId) => visibleColumns.has(id);

  return (
    <Table.Row key={group.slug} index={index} id={group.slug}>
      <Table.Cell>
        <div className="flex items-center gap-2">
          {group.parent_slug !== CUSTOMER_GROUP_BASE_SLUG && <Icon name="inherit" size="sm" />}
          <div className="flex flex-col">
            <Text size="md">{group.name}</Text>
            <Text variant="secondary" size="sm">
              {t('features:customer_group.components.customer_groups_table.row.handle', {
                handle: group.slug,
              })}
            </Text>
          </div>
        </div>
      </Table.Cell>
      {show('tags') && (
        <Table.Cell>
          {tags.map(({ tag, link }) =>
            link ? (
              <a key={tag} href={link} target="_blank" rel="noopener noreferrer">
                <Badge variant="green" interactive>
                  {tag}
                </Badge>
              </a>
            ) : (
              <Badge key={tag} variant="green">
                {tag}
              </Badge>
            ),
          )}
        </Table.Cell>
      )}
      {show('priceLists') && (
        <Table.Cell>
          <PriceListBadges group={group} />
        </Table.Cell>
      )}
      {show('paymentMethods') && (
        <Table.Cell>
          <MethodTooltipWrapper
            parentName={
              group.inheritList?.includes('allowedPaymentMethods') ? group.parent_name : undefined
            }
          >
            <ResultsElement
              isInherited={group.inheritList?.includes('allowedPaymentMethods')}
              data={group?.paymentMethods}
            />
          </MethodTooltipWrapper>
        </Table.Cell>
      )}
      {show('orderLimits') && (
        <Table.Cell>
          <OrderLimitsBadges group={group} />
        </Table.Cell>
      )}
      {show('otherSettings') && (
        <Table.Cell>
          <MethodTooltipWrapper
            parentName={
              group.inheritList?.includes('overwrittenSettings') ? group.parent_name : undefined
            }
          >
            {/* Hidden span displays empty inheritance for parents with undefined overwrittenSettings, since MethodTooltipWrapper returns null for undefined children */}
            {group.overwrittenSettings ? (
              <Badge variant="yellow">{String(group.overwrittenSettings)}</Badge>
            ) : (
              <span className="hidden" />
            )}
          </MethodTooltipWrapper>
        </Table.Cell>
      )}
      <Table.Cell sticky>
        <Button
          variant="secondary"
          onClick={() => navigationHandler.navigate(`/customers/groups/edit/${group.slug}`)}
          collapsible
        >
          {t('buttons:edit')}
        </Button>
      </Table.Cell>
    </Table.Row>
  );
}

export function CustomerGroupsTable({
  inheritedGroups,
  loading,
  visibleColumns,
}: {
  inheritedGroups: CustomerGroup[];
  loading?: boolean;
  visibleColumns: Set<ColumnId>;
}) {
  const { platform } = useEnv();
  const headings = TABLE_COLUMNS.filter((col) => visibleColumns.has(col.id));

  return (
    <Table
      caption={t('features:customer_group.components.customer_groups_table.caption')}
      headings={headings.map((heading) => {
        const key = resolveColumnHeadingKey(heading, platform);
        return key ? t(key) : '';
      })}
      loading={loading}
    >
      {inheritedGroups?.map((group, index) => (
        <CustomerGroupsTableRow
          key={group.slug}
          group={group}
          index={index}
          visibleColumns={visibleColumns}
        />
      ))}
    </Table>
  );
}
